import { navigate } from '../../app/navigation';
import type { ReportReadiness } from '../../domain/reportReadiness';
import { useT } from '../../i18n/i18n';
import { areaDisplayName, descriptorLabel, itemTitleFor } from '../../i18n/labels';
import type { InspectionState } from '../../state/inspectionStore';
import { ListRow, SectionHeader } from '../../ui/components';

// NY F1 Round 2 (OC-2): what the final client report still needs, one row per unresolved mandatory
// descriptor, each opening the exact place it is answered — the item sheet of the row that carries
// it, or the inspection details for a property fact. Shared by the Report and Closeout screens so
// both read the same canonical readiness (src/state/reportReadiness.ts). Factual and specific: it
// names the descriptor and the row; it never states a rule or a statute.
export function ReadinessList(props: { inspectionId: string; readiness: ReportReadiness; state: InspectionState; testId: string }) {
  const { t, tp, locale } = useT();
  const areaById = new Map(props.state.areas.map((a) => [a.id, a] as const));
  const itemById = new Map(props.state.items.map((i) => [i.id, i] as const));
  return (
    <div className="list list-readiness" data-testid={props.testId}>
      <SectionHeader title={t('readiness.title')} meta={tp('readiness.blocked', props.readiness.blockers.length)} />
      {props.readiness.blockers.map((blocker, index) => {
        const area = blocker.areaId ? areaById.get(blocker.areaId) : undefined;
        const item = blocker.itemId ? itemById.get(blocker.itemId) : undefined;
        const where = blocker.kind === 'propertyFact' ? t('readiness.propertyFact') : `${area ? areaDisplayName(locale, area) : ''}${item ? ` · ${itemTitleFor(locale, item)}` : ''}`;
        const label = descriptorLabel(t, blocker.key);
        return (
          <ListRow
            key={`${blocker.key}-${blocker.itemId ?? 'property'}-${index}`}
            icon="priority_high"
            iconTone="error"
            title={label}
            subtitle={where}
            trailing={<span className="badge badge-muted">{t('readiness.fix')}</span>}
            chevron
            onClick={() =>
              blocker.kind === 'propertyFact' || !blocker.areaId
                ? navigate({ screen: 'inspectionDetails', inspectionId: props.inspectionId })
                : navigate({ screen: 'areaWorkspace', inspectionId: props.inspectionId, areaId: blocker.areaId, itemSheetId: blocker.itemId, focusDescriptor: blocker.key })
            }
            dataName={label}
            testId="readiness-row"
          />
        );
      })}
    </div>
  );
}
